import { HttpClient } from '@angular/common/http';
import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/services/auth.service';
import { ClockSyncService } from '../../core/services/clock-sync.service';
import { LoggerService } from '../../core/services/logger.service';
import { SessionStorageService } from '../../core/services/session-storage.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

interface InvitationDetails {
  email: string;
  role: string;
  status: string;
  organizationName: string;
  inviterName: string;
  inviterEmail: string;
  expiresAt: string;
}

type AcceptInvitationResult =
  | { success: true; organizationName: string; customerId: string; role: string }
  | { success: false; reason: string };

@Component({
  selector: 'app-accept-invitation',
  standalone: true,
  imports: [ButtonComponent, IconComponent, TranslatePipe],
  templateUrl: './accept-invitation.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './accept-invitation.component.css',
})
export class AcceptInvitationComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private sessionStorage = inject(SessionStorageService);
  private logger = inject(LoggerService).getLogger('accept-invitation');
  private clockSync = inject(ClockSyncService);

  state = signal<'loading' | 'preview' | 'accepting' | 'success' | 'error'>('loading');
  invitation = signal<InvitationDetails | null>(null);
  errorMessage = signal('');
  token = '';

  async ngOnInit() {
    this.logger.debug('Component initialized');
    this.token = this.route.snapshot.queryParams['token'];
    this.logger.debug('Token from URL:', this.token);

    if (!this.token) {
      this.state.set('error');
      this.errorMessage.set('Invalid invitation link');
      return;
    }

    // If authenticated and returning from OAuth, accept the invitation
    if (this.authService.isAuthenticated()) {
      this.logger.debug('User is authenticated');
      const storedToken = this.sessionStorage.getItem('invitation_token');
      this.logger.debug('Stored token:', storedToken);
      if (storedToken === this.token) {
        this.logger.debug('Tokens match - accepting invitation');
        this.sessionStorage.removeItem('invitation_token');
        await this.acceptInvitation();
        return;
      }
    }

    // Load invitation details (public endpoint, no auth required)
    this.logger.debug('Loading invitation details (no auth required)');
    await this.loadInvitationDetails();
  }

  private async loadInvitationDetails() {
    try {
      const details = await this.http
        .get<InvitationDetails>(`${environment.apiUrl}/invitations/${this.token}`)
        .toPromise();

      if (!details) {
        throw new Error('No response from server');
      }

      this.invitation.set(details);

      // Check status
      if (details.status === 'expired') {
        this.state.set('error');
        this.errorMessage.set('This invitation has expired');
        return;
      }

      if (details.status !== 'pending') {
        this.state.set('error');
        this.errorMessage.set('This invitation has already been used');
        return;
      }

      this.state.set('preview');
    } catch (err: any) {
      this.state.set('error');
      this.errorMessage.set(err.error?.error || 'Failed to load invitation details');
      this.logger.error('Load invitation error:', err);
    }
  }

  acceptWithGoogle() {
    // Pass invitation token directly to auth service
    // Backend will handle acceptance after OAuth completes
    this.authService.login(this.token);
  }

  async acceptWithTestAuth() {
    // For test users, navigate to test login with invitation token
    this.sessionStorage.setItem('invitation_token', this.token);
    this.router.navigate(['/login'], {
      queryParams: { invitation: this.token },
    });
  }

  decline() {
    this.router.navigate(['/landing']);
  }

  private async acceptInvitation() {
    this.state.set('accepting');

    try {
      const result = await this.http
        .post<AcceptInvitationResult>(`${environment.apiUrl}/invitations/${this.token}/accept`, {})
        .toPromise();

      if (!result) {
        throw new Error('No response from server');
      }

      if (result.success) {
        this.state.set('success');

        // Redirect to home after 2 seconds
        setTimeout(() => {
          this.router.navigate(['/']);
        }, 2000);
      } else {
        this.state.set('error');
        this.errorMessage.set(this.getErrorMessage(result.reason));
      }
    } catch (err: any) {
      this.state.set('error');
      this.errorMessage.set(err.error?.message || 'Failed to accept invitation');
      this.logger.error('Acceptance error:', err);
    }
  }

  private getErrorMessage(reason: string): string {
    const messages: Record<string, string> = {
      not_found: 'Invitation not found or already used',
      already_used: 'This invitation has already been accepted',
      expired: 'This invitation has expired',
      email_mismatch: 'This invitation is for a different email address',
      already_member: 'You are already a member of this organization',
    };

    return messages[reason] || 'Unable to accept invitation';
  }

  getTimeRemaining(expiresAt: string): string {
    const now = new Date(this.clockSync.now());
    const expires = new Date(expiresAt);
    const diffMs = expires.getTime() - now.getTime();

    if (diffMs <= 0) {
      return 'expired';
    }

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours} hour${hours === 1 ? '' : 's'} ${minutes} minute${minutes === 1 ? '' : 's'}`;
    }

    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
}
